<script>
  import {
    Navbar,
    NavbarItem,
    NavbarZone,
    DropdownItem,
    DropdownList,
    DropdownDivider,
    Icon,
    Button,
    Text,
    Flex,
    FlexItem,
  } from "@gl/design-system";
  import { Link } from "svelte-routing";
  import { _, user, contract, url, navbarItems, Auth } from "../stores/index";
  import { needsFullReloadInto, getDocsApps, isWithinPath } from "../utils";
  import { format, subMinutes, isBefore } from "date-fns";
  import {
    faBuilding,
    faArrowRight,
    faCopy,
    faArrowUpRightFromSquare,
  } from "@fortawesome/pro-regular-svg-icons";
  import { onMount, onDestroy, getContext } from "svelte";
  import { getIDP } from "../services/IDP";
  const IDP = getIDP();

  const authClass = new Auth(user);
  const { isAuthorized } = authClass;

  async function logOut() {
    try {
      await IDP.logout();
    } catch (e) {
      console.error("Logout failed", e);
    }
  }

  function showApp(navbarItem) {
    if (!$user.apps?.some((ua) => ua.id === navbarItem)) return false;

    if (navbarItem === "channel-manager" && !$isAuthorized("channel_access"))
      return false;
    if (navbarItem === "cut-manager" && !$isAuthorized("cut_access"))
      return false;
    if (navbarItem === "access-manager" && !$isAuthorized("user_read"))
      return false;
    return true;
  }

  let isHelpOpen = false;

  $: hasDocs = getDocsApps($user?.apps, $isAuthorized).length > 0;

  function handleNavClick(event, navbarItem) {
    if (needsFullReloadInto(navbarItem, $url.pathname)) {
      event.preventDefault();
      window.location.assign(`/${navbarItem}/contracts/${$contract?.id}`);
    }
  }

  let now = new Date();
  function updateDate() {
    now = new Date();
    const tokenExpiration = new Date($user?.token?.exp);
    const oneMinBeforeExpiration = subMinutes(tokenExpiration, 1);

    if (
      isBefore(tokenExpiration, now) ||
      isBefore(oneMinBeforeExpiration, now)
    ) {
      IDP.getToken();
    }
  }
  const interval = setInterval(updateDate, 1000);

  const config = getContext("config");

  onMount(() => {
    if (!$user.token) return true;
  });

  onDestroy(() => {
    clearInterval(interval);
  });
</script>

<div class="navbar-container">
  <Navbar logoHref={config.REDIRECT_URI}>
    <div slot="navbar-main">
      <Link to={`/contracts/${$contract?.id}`} let:active>
        <span
          class={active
            ? "mr-2 level-6 color-white is-selected"
            : "mr-2 level-6 color-white"}
        >
          {config.NAME}
        </span>
      </Link>
    </div>

    <NavbarZone position="start">
      <span class="vertical-line mx-3" />
      {#each $navbarItems || [] as navbarItem}
        {#if $user && showApp(navbarItem)}
          <div class="navbar-items">
            <NavbarItem type="content">
              <Link
                to={`/${navbarItem}/contracts/${$contract?.id}`}
                on:click={(event) => handleNavClick(event, navbarItem)}
              >
                <span
                  class="gl-navbar-button"
                  class:is-selected={isWithinPath(
                    $url.pathname,
                    `/${navbarItem}`,
                  )}
                >
                  {$_(navbarItem)}
                </span>
              </Link>
            </NavbarItem>
          </div>
        {/if}
      {/each}
    </NavbarZone>
    <NavbarZone position="end">
      <div class="time-date" role="timer">
        <p class="time">{now.toLocaleTimeString()}</p>
        <p class="date">
          {format(now, "dd.MM.yyyy")}
        </p>
        <div class="tooltip">
          {format(now, "'GMT' X")}
        </div>
      </div>

      {#if hasDocs || config.SUPPORT_LINK}
        <div class="vertical-line mx-3" />
        <NavbarItem type="dropdown" bind:selected={isHelpOpen}>
          <span slot="label">
            <span> {$_("common.help")}</span>
          </span>
          <DropdownList on:select>
            {#if hasDocs}
              <div
                class="gl-dropdown-item"
                class:is-selected={isWithinPath($url.pathname, "/docs")}
              >
                <Link to="/docs" on:click={() => (isHelpOpen = false)}>
                  {$_("docs.title")}
                </Link>
              </div>
            {/if}
            {#if config.SUPPORT_LINK}
              <DropdownItem>
                <Flex class="align-items-center">
                  <a
                    href={config.SUPPORT_LINK}
                    class="gl-navbar-item"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {$_("help.client.documentation")}
                  </a>
                  <Icon icon={faArrowUpRightFromSquare} />
                </Flex>
              </DropdownItem>
            {/if}
          </DropdownList>
        </NavbarItem>
      {/if}

      <div class="vertical-line mx-3" />

      {#if $contract}
        <NavbarItem type="dropdown">
          <span slot="label">
            <Icon icon={faBuilding} />
            <span>{$contract.name}</span>
          </span>
          <DropdownList on:select>
            <DropdownItem>
              <Flex>
                <Icon icon={faBuilding} />
                <div class="contract-name ml-2" title={$contract.name}>
                  {$contract.name}
                </div>
              </Flex>
              <Flex>
                <FlexItem>
                  <div
                    class="ml-1 mr-1 color-text-light contract-id"
                    title={$contract?.id}
                  >
                    <b class="mr-1">ID:</b>{$contract?.id}
                  </div>
                </FlexItem>
                <FlexItem>
                  <Button
                    on:click={() =>
                      navigator.clipboard.writeText($contract?.id)}
                    transparent
                    style="padding:0px; margin-top:-5px"
                    title="copy-button"
                  >
                    <Icon icon={faCopy} />
                  </Button>
                </FlexItem>
              </Flex>
            </DropdownItem>
            {#if $user?.contracts.length > 1 || $user?.data?.isMaster}
              <DropdownItem style="margin-left:0.25rem">
                <div class="color-grey ml-2" style="font-size: 0.8em">
                  {$_("contract.switch")}:
                </div>
                <div class="fake-button ml-4">
                  <Flex>
                    <Link to="/contracts">{$_("contract.select")}</Link>
                    <Icon icon={faArrowRight} />
                  </Flex>
                </div>
              </DropdownItem>
            {/if}
          </DropdownList>
        </NavbarItem>
        <div class="vertical-line mx-3" />
      {/if}

      <NavbarItem type="dropdown">
        <span slot="label">
          <Icon name="User" />
          <span>{$user?.data?.email}</span>
        </span>

        <DropdownList on:select>
          <div
            class="gl-dropdown-item"
            class:is-selected={$url.pathname.endsWith("me/data")}
          >
            <Link to={"me/data"}>
              {$_("users.me.profile")}
            </Link>
          </div>
          <DropdownDivider />
          <div
            class="gl-dropdown-item"
            class:is-selected={$url.pathname.endsWith("preferences")}
          >
            <Link to={"me/preferences"}>
              {$_("common.preferences")}
            </Link>
          </div>
          <DropdownDivider />

          <DropdownItem>
            <Button on:click={logOut} style="text-align:left"
              >{$_("common.logout")}</Button
            >
          </DropdownItem>

          <DropdownDivider />

          <DropdownItem>
            <Flex class="justify-content-center">
              <p class="color-text-light" style="font-size:14px">
                {$_("common.version")}: {import.meta.env.VITE_LATEST_TAG}
              </p>
            </Flex>
          </DropdownItem>
        </DropdownList>
      </NavbarItem>
    </NavbarZone>
  </Navbar>
</div>

<style>
  .navbar-container {
    min-height: 64px;
    position: sticky;
    top: 0;
    align-self: start;
    z-index: 40;
  }

  .fake-button {
    border: 1px solid var(--gl-primary);
    margin-bottom: 10px;
    width: 85%;
    font-weight: bold;
    color: var(--gl-primary);
  }

  .time {
    font-family: "Roboto";
    font-size: 16px;
    text-align: right;
    color: var(--gl-text);
  }

  .date {
    font-size: 12px;
    color: var(--gl-text-light);
    text-align: right;
  }

  .time-date:hover .tooltip {
    display: block;
  }

  .time-date {
    position: relative;
  }

  .tooltip {
    background-color: var(--gl-scheme-main);
    border-radius: 4px;
    color: var(--gl-text-light);
    text-align: center;
    right: 0;
    position: absolute;
    padding: 2px 8px;
    font-size: 14px;
    display: none;
    box-shadow:
      0px 8px 16px -2px rgba(0, 0, 0, 0.1),
      0px 0px 0px 1px rgba(0, 0, 0, 0.02);
  }

  .contract-id {
    font-size: 14px;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 146px;
  }

  /* tablet displays */
  @media screen and (max-width: 1023px) {
    .vertical-line {
      display: none;
    }

    .time-date {
      display: none;
    }
  }

  .contract-name {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: bold;
    text-wrap: nowrap;
  }
</style>
